<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import {
  DifficultyLevel,
  Language,
  TagKey,
  useTranslations,
} from '../composables/useTranslations';
import { Room } from '../interfaces/Room.interface';
import { BarChart2, Globe, Tag } from 'lucide-vue-next';

defineProps<{ room: Room }>();

const route = useRoute();

const t = useTranslations();

const detailsActiveTab = ref<'roomInfo' | 'share'>('roomInfo');

const shareUrl = computed(() => {
  return window.location.origin + route.fullPath;
});

const copyShareUrl = () => {
  navigator.clipboard
    .writeText(shareUrl.value)
    .then(() => {
      alert('Link copied to clipboard!');
    })
    .catch((err) => {
      console.error('Failed to copy: ', err);
    });
};
</script>

<template>
  <section
    id="room-info-tabs"
    class="flex flex-col min-h-0 bg-white rounded-2xl shadow-sm border border-gray-100"
  >
    <div class="border-b border-gray-100 shrink-0">
      <div class="flex">
        <button
          class="px-4 py-3 text-xs font-medium text-gray-500 hover:text-gray-700"
          :class="
            detailsActiveTab === 'roomInfo'
              ? 'border-b-2 border-[#dd7631] text-[#dd7631]'
              : 'text-gray-500 hover:text-gray-700'
          "
          @click="detailsActiveTab = 'roomInfo'"
        >
          {{ t.roomInfo }}
        </button>
        <button
          class="px-4 py-3 text-xs font-medium text-gray-500 hover:text-gray-700"
          :class="
            detailsActiveTab === 'share'
              ? 'border-b-2 border-[#dd7631] text-[#dd7631]'
              : 'text-gray-500 hover:text-gray-700'
          "
          @click="detailsActiveTab = 'share'"
        >
          {{ t.share }}
        </button>
      </div>
    </div>

    <div
      v-if="detailsActiveTab === 'roomInfo'"
      id="room-info-tab-content"
      class="p-4 space-y-3 overflow-y-auto"
    >
      <div class="flex items-center gap-3">
        <div
          class="w-8 h-8 rounded-xl flex items-center justify-center bg-[#dd7631]/10 text-[#dd7631] shrink-0"
        >
          <Globe :size="16" />
        </div>
        <div>
          <p class="text-xs text-gray-500">{{ t.language }}</p>
          <p class="font-semibold text-gray-900 text-sm">
            {{ t[room.language as Language] }}
          </p>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <div
          class="w-8 h-8 rounded-xl flex items-center justify-center bg-[#dd7631]/10 text-[#dd7631] shrink-0"
        >
          <BarChart2 :size="16" />
        </div>
        <div>
          <p class="text-xs text-gray-500">{{ t.difficulty }}</p>
          <p class="font-semibold text-gray-900 text-sm">
            {{ t[room.difficulty as DifficultyLevel] }}
          </p>
        </div>
      </div>

      <div v-if="room.tags" class="flex items-center gap-3">
        <div
          class="w-8 h-8 rounded-xl flex items-center justify-center bg-[#dd7631]/10 text-[#dd7631] shrink-0"
        >
          <Tag :size="16" />
        </div>
        <div>
          <p class="text-xs text-gray-500">{{ t.tags }}</p>
          <p class="font-semibold text-gray-900 text-sm">
            {{ room.tags.map((tag) => t[tag as TagKey]).join(', ') }}
          </p>
        </div>
      </div>
    </div>

    <div
      v-if="detailsActiveTab === 'share'"
      id="share-tab-content"
      class="p-4 space-y-3 overflow-y-auto"
    >
      <!-- Share Room -->
      <div>
        <h3 class="font-semibold text-gray-800 text-sm mb-3">
          {{ t.shareRoom }}
        </h3>
        <div class="flex gap-2">
          <input
            type="text"
            :value="shareUrl"
            readonly
            class="flex-grow py-2 px-3 text-sm bg-gray-50 border border-gray-200 rounded-lg w-full"
          />
          <button
            class="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            @click="copyShareUrl"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path
                d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
              ></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
